import { useEffect, useRef } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from '@/i18n/translations';
import { isUuid } from '@leanin/shared';
import { trackResourceViewed } from '@/lib/analytics/events';
import { EDITABLE_TITLE_RELOAD } from '@/lib/analytics/page-meta';
import { useMember } from '@/lib/use-session';
import { loadResourceMeta, resourcesKeys, useResourceDetail } from '@/lib/queries/resources';
import { useServerBodyHandoff, useServerBodyRecovery } from '@/lib/server-body-handoff';
import { LoadFailure } from '@/components/states/index';
import {
  ResourceArticleView,
  type RelatedResources,
} from './-components/ResourceArticleView';

export const Route = createFileRoute('/_public/resources/$id/')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  loader: {
    handler: ({ params, context: { queryClient } }) => loadResourceMeta(queryClient, params.id),
    staleReloadMode: EDITABLE_TITLE_RELOAD,
  },
  pendingComponent: ResourceDetailPending,
  component: ResourceDetailPage,
});

function ResourceDetailPending() {
  return (
    <ResourceArticleView
      resource={null}
      related={{ status: 'pending' }}
      signedIn={false}
      viewerIsAdmin={false}
      viewerCanEdit={false}
    />
  );
}

function ResourceDetailPage() {
  const { id } = Route.useParams();
  const me = useMember();
  const t = useTranslations('Resources');

  // The loader already ran the fetch, so a failure it left behind is not
  // refetched on mount: the retry surface refetches it on demand.
  const resourceQuery = useResourceDetail(id, { retryOnMount: false });
  const resource = resourceQuery.data ?? null;
  const relatedQuery = useQuery({
    ...resourcesKeys.related(id, { tagSlug: resource?.tags[0]?.slug }),
    enabled: resource !== null,
  });
  const related: RelatedResources =
    relatedQuery.status === 'success'
      ? { status: 'success', items: relatedQuery.data }
      : relatedQuery.status === 'error'
        ? { status: 'error', error: relatedQuery.error }
        : { status: 'pending' };
  // Finished means no skeleton left on the page: the article and its related
  // list both settled. A failed related list is settled too and hands off
  // with its error in the aside: the server body stands in for the article,
  // so only the article's failure keeps it (`LoadFailure`, after which a
  // kept page tries the article again).
  useServerBodyHandoff(resourceQuery.isSuccess && related.status !== 'pending');
  useServerBodyRecovery(resourceQuery);

  const viewedResourceIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (resource === null || viewedResourceIdRef.current === resource.id) return;
    viewedResourceIdRef.current = resource.id;
    trackResourceViewed({ content_type: resource.kind });
  }, [resource]);

  // A signed-out visitor's 404 may be a members-only resource, so the
  // not-found copy says signing in may help without claiming it exists.
  if (resourceQuery.isError) {
    return (
      <LoadFailure
        error={resourceQuery.error}
        loadError={{ title: t('detail.loadError.title'), body: t('detail.loadError.body') }}
        signedOutNotFound={{
          title: t('detail.signedOutNotFound.title'),
          body: t('detail.signedOutNotFound.body'),
        }}
      />
    );
  }

  const signedIn = me !== null;
  const viewerIsAdmin = me !== null && me.platformRole === 'admin';
  const viewerCanEdit = viewerIsAdmin;

  return (
    <ResourceArticleView
      resource={resource}
      related={related}
      signedIn={signedIn}
      viewerIsAdmin={viewerIsAdmin}
      viewerCanEdit={viewerCanEdit}
    />
  );
}
